import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { setSession } from './utils';
import { mutate } from 'swr';
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------
const initialState = {
  user: null,
  loading: true,
};


const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

export function AuthProvider({ children }) {
  const [ state, dispatch ] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      if (accessToken) {
        setSession(accessToken);

        const response = await axios.get(endpoints.auth.me);
        console.log(response, 'here is the response coming');
        const data = response.data.payload || response.data.payload?.admin;
        console.log("🚀 ~ initialize ~ data:", data)
        dispatch({
          type: 'INITIAL',
          payload: {
            user: data,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [ initialize ]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const apiData = {
      email,
      password,
    };

    const response = await axios.post(endpoints.auth.login, apiData);
    console.log(response, 'response console');
    const data = response.data.payload || response.data.payload.admin;
    console.log(data, "data rha ha");
    setSession(data?.token);
    dispatch({
      type: 'LOGIN',
      payload: {
        user: data,
      },
    });
    return data;
  }, []);
  const refreshUser = useCallback(async () => {
    initialize();
  }, []);

  const verifyOtp = useCallback(async (email, code) => {
    try {
      const apiData = { email, code };
      const response = await axios.post(endpoints.auth.verify_otp, apiData, {
        headers: { 'Content-Type': 'application/json' },
      });
      console.log("API Response:", response);
      if (response.status === 200) {
        console.log("✅ OTP verification successful, fetching user profile...");
        setSession(response.data.payload.token);
        const profileResponse = await axios.get(endpoints.auth.me);
        console.log("User Profile Response:", profileResponse);
        const userData = profileResponse.data.payload || profileResponse.data.payload?.admin;
        dispatch({
          type: 'LOGIN',
          payload: {
            user: userData,
          },
        });
        return { success: true };
      } else {
        return { success: false, message: 'Invalid OTP' };
      }
    } catch (error) {
      console.error("❌ OTP Verification Error:", error);
      return { success: false, message: error.response?.data?.message || "Something went wrong" };
    }
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
    };

    const response = await axios.post(endpoints.auth.register, data);

    const { accessToken, user } = response.data;

    localStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  //forgot password
  const forgotPassword = useCallback(async (email, companyId) => {
    const response = await axios.post(endpoints.auth.forgotPassword, { email, companyId });
    console.log(response);
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    localStorage.clear();
    mutate(() => true, undefined, { revalidate: false });
    dispatch({
      type: 'LOGOUT',
    });
  }, []);


  //new password
  const newPassword = useCallback(async (email, companyId, otpCode, password) => {
    const response = await axios.post(endpoints.auth.newPassword, { email, companyId, otpCode, password });
    return response;
  }, []);


  //get company list
  const getCompanyList = useCallback(async (email) => {
    const response = await axios.post(endpoints.auth.getCompanyList, { email });
    return response;
  }, []);


  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      verifyOtp,
      logout,
      refreshUser,
      forgotPassword,
      newPassword,
      getCompanyList,
    }),
    [ login, logout, refreshUser, register, state.user, status, forgotPassword, newPassword, getCompanyList, verifyOtp ]
  );

  return <AuthContext.Provider value={ memoizedValue }>{ children }</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
